import { PackageEvidence } from '@cadstrom/cadstrom-shared';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { PinnablePreviewTooltip } from '#components/pinnable-preview-tooltip/pinnable-preview-tooltip.js';
import { useTRPC } from '#src/trpc.js';

import type { PopperPlacementType } from '@mui/material/Popper';

const THUMBNAIL_SKELETON_HEIGHT = 128;

const THUMBNAIL_BUTTON_CLASS =
	'w-full rounded-md focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-blue-9';
const PREVIEW_FALLBACK_PLACEMENTS: readonly PopperPlacementType[] = ['right', 'bottom', 'top'];
const PREVIEW_UNAVAILABLE = 'Preview unavailable';
const OCR_VERSION_MISMATCH_MESSAGE =
	'Preview unavailable: this region was recorded against a different OCR version of the datasheet.';

/** Props for {@link EvidenceThumbnail}. */
export interface EvidenceThumbnailProps {
	/** File-store identifier of the datasheet PDF. */
	fileId: string;
	/** The evidence address of the region to show: an element's crop or a whole page. */
	locatorAddress: string;
	/** Alternative text for the rendered image. */
	alt: string;
	/** When given, the enlarged preview offers to open the datasheet. */
	onOpenDatasheet?: () => void;
	/** When given, pressing the image calls it at once, in place of the enlarged preview. */
	onSelect?: () => void;
}

/**
 * A full-width image of a recorded evidence region, read from its stored preview. Hovering or focusing it shows an
 * enlarged preview; clicking pins the preview open until it is clicked again, clicked away from, or dismissed with
 * Escape. Pressing it can instead act at once, with no enlarged preview. Without an image it says the preview is
 * unavailable, and says why when the region was recorded against a different OCR version of the datasheet.
 */
export function EvidenceThumbnail({ fileId, locatorAddress, alt, onOpenDatasheet, onSelect }: EvidenceThumbnailProps) {
	const [failedImage, setFailedImage] = useState<string | null>(null);
	const trpc = useTRPC();

	const previewQuery = useQuery(
		trpc.packageEvidence.locatorPreview.queryOptions(
			{ fileId, locatorAddress },
			{ gcTime: Infinity, staleTime: Infinity, throwOnError: false },
		),
	);
	const base64Image = previewQuery.data?.base64Image;
	const imageSource = base64Image ? `data:image/png;base64,${base64Image}` : undefined;
	const showImage = imageSource !== undefined && failedImage !== imageSource;

	if (previewQuery.isPending) {
		return (
			<Skeleton
				variant='rounded'
				height={THUMBNAIL_SKELETON_HEIGHT}
				aria-label='Loading evidence preview'
				className='w-full'
			/>
		);
	}

	if (!showImage) {
		const isOcrVersionMismatch =
			previewQuery.data?.unavailableReason === PackageEvidence.EvidencePreviewUnavailableReason.OcrVersionMismatch;
		return (
			<p className='text-gray-a10 italic'>
				{isOcrVersionMismatch ? OCR_VERSION_MISMATCH_MESSAGE : PREVIEW_UNAVAILABLE}
			</p>
		);
	}

	const image = (
		<img
			src={imageSource}
			alt={alt}
			onError={() => setFailedImage(imageSource)}
			className='block max-h-64 w-full rounded-md border border-gray-6 bg-white object-contain'
		/>
	);

	if (onSelect !== undefined) {
		return (
			<Tooltip title='Show in datasheet' describeChild>
				<ButtonBase onClick={onSelect} className={THUMBNAIL_BUTTON_CLASS}>
					{image}
				</ButtonBase>
			</Tooltip>
		);
	}

	return (
		<PinnablePreviewTooltip
			placement='left'
			fallbackPlacements={PREVIEW_FALLBACK_PLACEMENTS}
			title={(close) => (
				<div className='flex max-w-full flex-col gap-2'>
					<img src={imageSource} alt={alt} className='max-h-96 w-auto max-w-full rounded-md bg-white object-contain' />
					{onOpenDatasheet !== undefined && (
						<div className='flex justify-end'>
							<Button
								variant='outlined'
								startIcon={<PictureAsPdfIcon />}
								onClick={() => {
									close();
									onOpenDatasheet();
								}}
							>
								<span>Open in datasheet</span>
							</Button>
						</div>
					)}
				</div>
			)}
		>
			{(triggerProps) => (
				<ButtonBase {...triggerProps} className={THUMBNAIL_BUTTON_CLASS}>
					{image}
				</ButtonBase>
			)}
		</PinnablePreviewTooltip>
	);
}
